import React, { memo } from 'react';
import {
  Divider,
  SwipeableDrawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Theme,
  styled,
  useMediaQuery,
} from '@mui/material';
import moment, { MomentInput } from 'moment';
import { ORDER_STATUS, APPROVAL_STATUS, ORDER_ITEM_STATUS } from '../../constant';
import { mapOrderStatus } from '../../utils';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';
import Box from 'component/layoutComponents/Box/Box';
import Typography from '../../../Typography/Typography';
import { OrderDetails } from '../../types';
import EyeBrow from 'component/MarketPlace/eyeBrow/eyeBrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CancelRounded } from '@mui/icons-material';
import MenuContent from 'component/MarketPlace/OrderItemCard/components/MenuContent';


const OrderTitle = styled(Typography)(() => ({
  fontWeight: 'bold',
  wordBreak: 'break-all',
}));

const VerticalDivider = () => {
  return (
    <Box height="32px" borderColor="transparent" padding="0px 16px">
      <Divider orientation="vertical" />
    </Box>
  );
};

type Props = {
  orderDetails: OrderDetails;
  onCancelOrder?: () => void;
  isApprovalPage?: boolean;
  onDecline?: () => void;
  onApprove?: () => void;
  locale: any;
  userMarketplace?: Record<string, any>;
  statusTicketLocale?: string;
  pendingApprovalDelegatedLocale?: string;
};

const ActionsMenu = ({
  anchorEl,
  open,
  children,
  onClose,
  onOpen,
}: {
  anchorEl: Element | (() => Element) | null;
  open: boolean;
  children: React.ReactNode;
  onClose: () => void;
  onOpen: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const isMediumUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  if (isMediumUp) {
    return (
      <Menu anchorEl={anchorEl} open={open} onClose={onClose} disableScrollLock >
        {children}
      </Menu>
    );
  }

  return (
    <SwipeableDrawer onOpen={onOpen} anchor="bottom" open={open} onClose={onClose}>
      {children}
    </SwipeableDrawer>
  );
};

const ActionContent = ({
  onCancelOrder,
  orderDetails,
  locale,
  isApprovalPage,
  handleCloseActionMenu,
  onDecline,
  onApprove
}: {
  orderDetails: OrderDetails;
  onCancelOrder?: () => void;
  locale: any;
  isApprovalPage?: boolean;
  onDecline: () => void;
  onApprove: () => void;
  handleCloseActionMenu: () => void;
}) => {
  const isMediumDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  
  return (
    <Stack flexDirection={isMediumDown ? 'column-reverse' : 'column'}>
      {(orderDetails?.status === ORDER_STATUS.IN_PROGRESS ||
        orderDetails?.status === ORDER_STATUS.PENDING_APPROVAL) && (
          <>
            {!isApprovalPage && (<MenuItem key="1" onClick={onCancelOrder} id="li-orderdetails-cancel_order">
              <Box display="flex">
                {isMediumDown && (
                  <Box
                    display={isMediumDown ? 'flex' : 'block'}
                    paddingRight={4}
                  >
                    <CancelRounded color="error" />
                  </Box>
                )}
                <Typography
                  fontWeight={{ md: 600 }}
                  fontSize={{ md: 14 }}
                  color="error"
                >
                  {locale?.cancelOrder}
                </Typography>
              </Box>
            </MenuItem>)}
            {isApprovalPage && (
              <>
                <MenuContent
                  isApprovalPage={isApprovalPage}
                  onApprove={() => {
                    onApprove();
                    handleCloseActionMenu();
                  }}
                  onDecline={() => {
                    onDecline();
                    handleCloseActionMenu();
                  }}
                  locale={locale}
                  />                
              </>
            )}
          </>
        )}
    </Stack>
  );
};

const HeaderContent = ({
  orderDetails,
  onCancelOrder = () => {},
  isApprovalPage,
  locale,
  userMarketplace,
  statusTicketLocale,
  pendingApprovalDelegatedLocale,
  onApprove = () => {},
  onDecline = () => {}
}: Props) => {
  const orderDetailsStatus = (isApprovalPage && orderDetails?.approval?.approval_status === ORDER_ITEM_STATUS.PENDING && orderDetails?.approval?.assigned_approver !== userMarketplace?.email) ? orderDetails?.status + `_${APPROVAL_STATUS.DELEGATED}` : orderDetails?.status ;
  const orderStatus = orderDetailsStatus || '';
  const { statusText, statusColors: colors } = mapOrderStatus(orderStatus);
  const breakpoint = useBreakpoint();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openActionMenu = Boolean(anchorEl);

  const formatDate = (timestamp: MomentInput) => moment(timestamp).format('MMM. DD, YYYY');

  const handleClickActionMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const handleCancelOrder = () => {
    onCancelOrder();
    handleCloseActionMenu();
  };

  return (
    <Box mt={4} display="flex" justifyContent="space-between">
      <Box>
        <OrderTitle variant="h2">
          {locale?.order} #{orderDetails?._id?.slice(0, 5)}
        </OrderTitle>
        <Box
          display="flex"
          flexDirection={{ xxs: "column", sm: "row" }}
          alignItems={{ xxs: "flex-start", sm: "center" }}
          marginTop={{ xxs: "32px", md: "24px" }}
        >
          {
            orderDetails?.approval?.approval_status &&
            <>
              <Box>
                <EyeBrow
                  id="chip-orderdetails-status"
                  fontSizeContent={12}
                  color={colors.type}
                  label={(isApprovalPage && orderDetails?.approval?.approval_status === ORDER_ITEM_STATUS.PENDING && orderDetails?.approval?.assigned_approver !== userMarketplace?.email) ? (pendingApprovalDelegatedLocale || orderDetails?.status + `_${APPROVAL_STATUS.DELEGATED}`) : (statusTicketLocale || statusText)}
                  sx={{padding: '7px 0px', backgroundColor: colors.backgroundColor, color: colors.color, fontWeight: 600}}
                  variant="filled"
                />
              </Box>
              {breakpoint >= Breakpoint.sm && <VerticalDivider />}
              <Box display="flex" alignItems="center" mt={{ xxs: '12px', sm: 0 }}>
                <Typography variant="body2">
                  {locale?.created} {formatDate(orderDetails?.createdOn)}
                </Typography>
                {orderStatus && (
                  <>
                    {orderStatus === ORDER_STATUS.PENDING_APPROVAL ? (
                      <>
                        {/* <VerticalDivider />
                        <Box display="flex" alignItems="center">
                          <CommentSharp
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">1</Typography>
                        </Box>
                        <VerticalDivider />
                        <Box display="flex" alignItems="center">
                          <AttachmentOutlined
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">1</Typography>
                        </Box> */}
                        </>
                      ) : (
                        <>
                          <VerticalDivider />
                          <Typography variant="body2">
                            {locale?.place} {formatDate(orderDetails?.createdOn)}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
              </Box>
            </>
          }
        </Box>
      </Box>
      {orderStatus === ORDER_STATUS.PENDING_APPROVAL && (<Box alignSelf={{ sm: 'flex-end' }}>
        <IconButton onClick={handleClickActionMenu} aria-label="More Button" id="btn-orderdetals-click_more">
          <MoreVertIcon />
        </IconButton>
        <ActionsMenu
          anchorEl={anchorEl}
          open={openActionMenu}
          onClose={handleCloseActionMenu}
          onOpen={handleClickActionMenu}
        >
          <ActionContent
            onCancelOrder={handleCancelOrder}
            orderDetails={orderDetails}   
            locale={locale}
            isApprovalPage={isApprovalPage}
            handleCloseActionMenu={handleCloseActionMenu}
            onDecline={onDecline}
            onApprove={onApprove}
          />
        </ActionsMenu>
      </Box>)}
    </Box>
  );
};

export default memo(HeaderContent);
